import { Container, Stack, Typography } from '@mui/material';
import PageTitle from '../../components/PageTitle/PageTitle';
import { TermsConditionsHelmet } from './MyHelmet';
import { useEffect } from 'react';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../../config/firebase';

export default function TermsConditions() {
  // Firebase analytics
  useEffect(() => {
    logEvent(analytics, 'page_view', {
      page_title: 'Terms & Conditions',
      page_location: window.location.href,
      page_path: window.location.pathname,
    });
  }, []);
  return (
    <>
      <TermsConditionsHelmet />
      <PageTitle>
        <Stack
          component={'header'}
          gap={1}
          justifyContent="center"
          textAlign={'center'}
        >
          <Typography variant="h1" sx={{ fontSize: '40px', fontWeight: '700' }}>
            Terms & Conditions
          </Typography>
          <Stack>
            <Typography
              variant="body1"
              sx={{
                fontSize: '18px',
                color: 'primary.neutral550',
              }}
            >
              Last Updated Mar 7th, 2024
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '14px',
                color: 'primary.neutral550',
              }}
            >
              This page doesn't have a translation yet. Stay tuned.
            </Typography>
          </Stack>
        </Stack>
      </PageTitle>
      <Container
        maxWidth="lg"
        sx={{
          pb: 4,
          pt: 2,
          '& a': {
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          },
        }}
      >
        <section>
          <p>
            Welcome to Slex! These Terms and Conditions ("Agreement") govern
            your use of our website located at{' '}
            <a href="/" target="_new">
              https://www.slex.tv
            </a>{' '}
            (the "Site") and any services provided in connection with the Site.
            By accessing or using our Site, you agree to comply with these Terms
            and Conditions, which may be updated by Slex.tv from time to time.
            Any changes will be posted on the Site.
          </p>
          <Typography variant={'h2'} fontSize={'18px'} fontWeight="600">
            Privacy Policy
          </Typography>
          <p>
            We value your privacy and provide you with control over your
            personal information. Please refer to our Privacy Policy, accessible
            through the "privacy" link at the bottom of the page, for a complete
            statement of our privacy practices. The Privacy Policy is
            incorporated into this Agreement.
          </p>
          <Typography variant={'h2'} fontSize={'18px'} fontWeight="600">
            Age
          </Typography>
          <p>
            The site is intended only for users aged 18 or older. Individuals
            under the age of 18 are strictly prohibited from using the site and
            the accounts for any such person shall be terminated upon discovery.
          </p>
          <Typography variant={'h2'} fontSize={'18px'} fontWeight="600">
            Use of Software
          </Typography>
          <p>
            Company may make certain software available to you from the Site. If
            you download software from the Site, the software, including all
            files and images contained in or generated by the software, and
            accompanying data (collectively, “Software”) are deemed to be
            licensed to you by Company, for your personal, noncommercial, home
            use only. Company does not transfer either the title or the
            intellectual property rights to the Software, and Company retains
            full and complete title to the Software as well as all intellectual
            property rights therein. You may not sell, redistribute, or
            reproduce the Software, nor may you decompile, reverse-engineer,
            disassemble, or otherwise convert the Software to a
            human-perceivable form. All trademarks and logos are owned by
            Company or its licensors and you may not copy or use them in any
            manner.
          </p>
          <Typography variant={'h2'} fontSize={'18px'} fontWeight="600">
            User Content
          </Typography>
          <p>
            You grant Company a license to use the materials you post to the
            Site or Service. By posting, downloading, displaying, performing,
            transmitting, or otherwise distributing information or other content
            (“User Content”) to the Site or Service, you are granting Company,
            its affiliates, officers, directors, employees, consultants, agents,
            and representatives a license to use User Content in connection with
            the operation of the Internet business of Company, its affiliates,
            officers, directors, employees, consultants, agents, and
            representatives, including without limitation, a right to copy,
            distribute, transmit, publicly display, publicly perform, reproduce,
            edit, translate, and reformat User Content. You will not be
            compensated for any User Content. You agree that Company may publish
            or otherwise disclose your name in connection with your User
            Content. By posting User Content on the Site or Service, you warrant
            and represent that you own the rights to the User Content or are
            otherwise authorized to post, distribute, display, perform,
            transmit, or otherwise distribute User Content.
          </p>
          <Typography variant={'h2'} fontSize={'18px'} fontWeight="600">
            Compliance with Intellectual Property Laws
          </Typography>
          <p>
            When accessing the Site or using the Service, you agree to obey the
            law and to respect the intellectual property rights of others. Your
            use of the Service and the Site is at all times governed by and
            subject to laws regarding copyright ownership and use of
            intellectual property. You agree not to upload, download, display,
            perform, transmit, or otherwise distribute any information or
            content (collectively, “Content”) in violation of any third party’s
            copyrights, trademarks, or other intellectual property or
            proprietary rights. You agree to abide by laws regarding copyright
            ownership and use of intellectual property, and you shall be solely
            responsible for any violations of any relevant laws and for any
            infringements of third party rights caused by any Content you
            provide or transmit, or that is provided or transmitted using your
            User ID. The burden of proving that any Content does not violate any
            laws or third party rights rests solely with you.
          </p>
          <Typography variant={'h2'} fontSize={'18px'} fontWeight="600">
            Indemnification
          </Typography>
          <p>
            You agree to indemnify, defend, and hold harmless Company, its
            affiliates, officers, directors, employees, consultants, agents, and
            representatives from any and all third-party claims, losses,
            liability, damages, and/or costs (including reasonable attorney fees
            and costs) arising from your access to or use of the Site, your
            violation of these Terms and Conditions, or your infringement, or
            infringement by any other user of your account, of any intellectual
            property or other right of any person or entity. Company will notify
            you promptly of any such claim, loss, liability, or demand, and will
            provide you with reasonable assistance, at your expense, in
            defending any such claim, loss, liability, damage, or cost.
          </p>
          <Typography variant={'h2'} fontSize={'18px'} fontWeight="600">
            Termination
          </Typography>
          <p>
            Company may terminate or suspend your access to all or part of the
            Site, without notice, for any conduct that Company, in its sole
            discretion, believes is in violation of this Agreement or any
            applicable law or is harmful to the interests of another user or
            Company.
          </p>
          <Typography variant={'h2'} fontSize={'18px'} fontWeight="600">
            Disclaimer of Warranties
          </Typography>
          <p>
            THE SITE AND SERVICE ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE"
            BASIS. COMPANY EXPRESSLY DISCLAIMS ALL WARRANTIES OF ANY KIND,
            WHETHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, THE
            IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
            PURPOSE, AND NON-INFRINGEMENT. COMPANY MAKES NO WARRANTY THAT THE
            SITE OR SERVICE WILL BE UNINTERRUPTED, TIMELY, SECURE, OR
            ERROR-FREE, THAT DEFECTS WILL BE CORRECTED, OR THAT THE SITE OR THE
            SERVER THAT MAKES IT AVAILABLE ARE FREE OF VIRUSES OR OTHER HARMFUL
            COMPONENTS. COMPANY DOES NOT WARRANT OR MAKE ANY REPRESENTATIONS
            REGARDING THE USE OR THE RESULTS OF THE USE OF THE SITE OR SERVICE
            IN TERMS OF THEIR CORRECTNESS, ACCURACY, RELIABILITY, OR OTHERWISE.
            YOU (AND NOT COMPANY) ASSUME THE ENTIRE COST OF ALL NECESSARY
            SERVICING, REPAIR, OR CORRECTION. APPLICABLE LAW MAY NOT ALLOW THE
            EXCLUSION OF IMPLIED WARRANTIES, SO THE ABOVE EXCLUSION MAY NOT
            APPLY TO YOU.
          </p>
          <Typography variant={'h2'} fontSize={'18px'} fontWeight="600">
            Limitation of Liability
          </Typography>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL
            COMPANY, ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES,
            CONSULTANTS, AGENTS, OR REPRESENTATIVES BE LIABLE FOR ANY DIRECT,
            INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES,
            INCLUDING, BUT NOT LIMITED TO, DAMAGES FOR LOSS OF PROFITS,
            GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES (EVEN IF COMPANY HAS
            BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES), RESULTING FROM:
            (I) THE USE OR INABILITY TO USE THE SITE OR SERVICE; (II) THE COST
            OF PROCUREMENT OF SUBSTITUTE GOODS AND SERVICES RESULTING FROM ANY
            GOODS, DATA, INFORMATION, OR SERVICES PURCHASED OR OBTAINED OR
            MESSAGES RECEIVED OR TRANSACTIONS ENTERED INTO THROUGH OR FROM THE
            SITE OR SERVICE; (III) UNAUTHORIZED ACCESS TO OR ALTERATION OF YOUR
            TRANSMISSIONS OR DATA; (IV) STATEMENTS OR CONDUCT OF ANY THIRD PARTY
            ON THE SITE OR SERVICE; OR (V) ANY OTHER MATTER RELATING TO THE SITE
            OR SERVICE. IN NO EVENT SHALL COMPANY'S TOTAL LIABILITY TO YOU FOR
            ALL DAMAGES, LOSSES, OR CAUSES OF ACTION ARISING UNDER THIS
            AGREEMENT EXCEED THE AMOUNT PAID BY YOU, IF ANY, FOR ACCESSING THE
            SITE OR SERVICE.
          </p>
          <Typography variant={'h2'} fontSize={'18px'} fontWeight="600">
            Entire Agreement
          </Typography>
          <p>
            This Agreement constitutes the entire agreement between you and
            Company regarding the use of the Site and Service. The failure of
            Company to exercise or enforce any right or provision of this
            Agreement shall not operate as a waiver of such right or provision.
            The section titles in this Agreement are for convenience only and
            have no legal or contractual effect. This Agreement operates to the
            fullest extent permissible by law. If any provision of this
            Agreement is unlawful, void, or unenforceable, that provision is
            deemed severable from this Agreement and does not affect the
            validity and enforceability of any remaining provisions.
          </p>
          <p>
            By continuing to access or use Slex.tv, you acknowledge that you
            have read, understood, and agreed to be bound by these Terms and
            Conditions. If you do not agree to these terms, please discontinue
            your use of the website.
          </p>
          <Typography
            variant={'h2'}
            fontSize={'18px'}
            fontWeight="600"
          ></Typography>
          Amendments
          <p>
            Slex.tv reserves the right to amend these Terms and Conditions at
            any time. Any amendments will be effective immediately upon posting
            on the website. It is your responsibility to review the Terms and
            Conditions periodically for any changes. Your continued use of the
            website after any amendments constitutes your acceptance of the
            revised Terms and Conditions.
          </p>
          <Typography variant={'h2'} fontSize={'18px'} fontWeight="600">
            Governing Law and Jurisdiction
          </Typography>
          <p>
            These Terms and Conditions shall be governed by and construed in
            accordance with the laws of the jurisdiction where Slex.tv is
            registered. Any legal actions or proceedings arising out of or
            relating to these Terms and Conditions shall be exclusively subject
            to the jurisdiction of the courts in that jurisdiction.
          </p>
          <Typography variant={'h2'} fontSize={'18px'} fontWeight="600">
            Severability
          </Typography>
          <p>
            If any provision of these Terms and Conditions is found to be
            invalid, illegal, or unenforceable, the remaining provisions shall
            continue in full force and effect.
          </p>
          <Typography variant={'h2'} fontSize={'18px'} fontWeight="600">
            Waiver
          </Typography>
          <p>
            The failure of Slex.tv to enforce any right or provision of these
            Terms and Conditions shall not constitute a waiver of such right or
            provision unless acknowledged and agreed to by Slex.tv in writing.
          </p>
          <Typography variant={'h2'} fontSize={'18px'} fontWeight="600">
            Contact Information
          </Typography>
          <p>
            If you have any questions or concerns about these Terms and
            Conditions, you may contact Slex.tv at{' '}
            <a href="mailto:contact@slex.tv">contact@slex.tv</a>
          </p>
        </section>
      </Container>
    </>
  );
}
